import { useState, useMemo, useEffect } from "react";
import { Send, Users, Filter, Clock, RotateCw } from "lucide-react";
import { useCanAccess, useNotify } from "ra-core";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { useRealEstateContext } from "../providers/RealEstateContext";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { ActionPopupModal } from "../misc/ActionPopupModal";
import {
  campaignBudgetFilterMatches,
  normalizeWhatsAppCampaignTemplates,
  type WhatsAppCampaignTemplate,
} from "@/types/realEstate";
import { Link } from "react-router";

export const WhatsAppBroadcast = () => {
  const {
    campaignAudience,
    refreshCampaignAudience,
    triggerWhatsAppMessage,
    runStalenessReassignment,
    salesAgents,
  } = useRealEstateContext();
  const config = useConfigurationContext() as ReturnType<
    typeof useConfigurationContext
  > & { whatsappCampaignTemplates?: unknown };
  const notify = useNotify();
  const { canAccess: isAdmin } = useCanAccess({
    resource: "configuration",
    action: "edit",
  });

  const templates: WhatsAppCampaignTemplate[] = useMemo(
    () => normalizeWhatsAppCampaignTemplates(config.whatsappCampaignTemplates),
    [config.whatsappCampaignTemplates],
  );
  const noteStatuses = config.noteStatuses || [];

  useEffect(() => {
    if (!isAdmin) return;
    void refreshCampaignAudience();
  }, [refreshCampaignAudience, isAdmin]);

  const [selectedBudget, setSelectedBudget] = useState("all");
  const [selectedType, setSelectedType] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedAgent, setSelectedAgent] = useState("all");
  const [selectedLocation, setSelectedLocation] = useState("all");
  const [requirePhone, setRequirePhone] = useState(true);
  const [inactivityFilter, setInactivityFilter] = useState("15");
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [busy, setBusy] = useState(false);
  const [popupInfo, setPopupInfo] = useState<{
    title: string;
    desc: string;
    variant?: "success" | "info" | "whatsapp";
  } | null>(null);

  useEffect(() => {
    if (templates.length === 0) return;
    if (!templates.some((t) => t.id === selectedTemplateId)) {
      setSelectedTemplateId(templates[0].id);
    }
  }, [templates, selectedTemplateId]);

  const locationOptions = useMemo(() => {
    const set = new Set<string>();
    for (const lead of campaignAudience) {
      if (lead.preferred_location) set.add(lead.preferred_location);
    }
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [campaignAudience]);

  const filteredAudience = useMemo(() => {
    return campaignAudience.filter((lead) => {
      const matchesBudget = campaignBudgetFilterMatches(
        selectedBudget,
        lead.budget_min,
        lead.budget_max,
      );
      const matchesType =
        selectedType === "all" || lead.preferred_type === selectedType;
      const matchesStatus =
        selectedStatus === "all" ||
        lead.status === selectedStatus ||
        (!lead.status && selectedStatus === "unset");
      const matchesAgent =
        selectedAgent === "all" ||
        (selectedAgent === "unassigned"
          ? lead.sales_id == null
          : String(lead.sales_id) === selectedAgent);
      const matchesLocation =
        selectedLocation === "all" ||
        lead.preferred_location === selectedLocation;
      const matchesPhone = !requirePhone || lead.has_phone;
      const minInactivity = Number(inactivityFilter);
      const matchesInactivity = lead.last_contacted_days_ago >= minInactivity;
      return (
        matchesBudget &&
        matchesType &&
        matchesStatus &&
        matchesAgent &&
        matchesLocation &&
        matchesPhone &&
        matchesInactivity
      );
    });
  }, [
    campaignAudience,
    selectedBudget,
    selectedType,
    selectedStatus,
    selectedAgent,
    selectedLocation,
    requirePhone,
    inactivityFilter,
  ]);

  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId);

  const handleBroadcast = async () => {
    const template = selectedTemplate;
    const body = template?.body?.trim();
    if (!template || !body) {
      notify("Select or create a campaign template in Settings first.", {
        type: "warning",
      });
      return;
    }
    const targets = filteredAudience.filter((lead) => Boolean(lead.phone));
    if (targets.length === 0) {
      notify("No leads with a phone number in this segment.", {
        type: "warning",
      });
      return;
    }

    setBusy(true);
    let sent = 0;
    let failed = 0;
    let lastError = "";
    try {
      for (const lead of targets) {
        const msg = `Namaste ${lead.name}! ${body}`;
        const contactId = /^\d+$/.test(String(lead.id)) ? lead.id : null;
        try {
          await triggerWhatsAppMessage(
            lead.phone,
            msg,
            false,
            contactId,
            "campaign",
            template.media_url,
          );
          sent += 1;
        } catch (e) {
          failed += 1;
          lastError = e instanceof Error ? e.message : String(e);
        }
      }

      if (failed === targets.length) {
        const desc =
          lastError ||
          "Campaign send was blocked. WhatsApp campaigns require a manager account.";
        notify(desc, { type: "warning" });
        setPopupInfo({
          title: "Broadcast blocked",
          desc,
          variant: "info",
        });
        return;
      }

      if (failed > 0) {
        const desc = `Sent ${sent} of ${targets.length}. ${failed} failed${lastError ? `: ${lastError}` : "."}`;
        notify(desc, { type: "warning" });
        setPopupInfo({
          title: "Partial broadcast",
          desc,
          variant: "info",
        });
        return;
      }

      setPopupInfo({
        title: "WhatsApp Bulk Broadcast Dispatched!",
        desc: `Dispatched Meta Cloud API messages to ${sent} segmented buyers.`,
        variant: "whatsapp",
      });
    } finally {
      setBusy(false);
    }
  };

  const handleRunStalenessRule = async () => {
    setBusy(true);
    try {
      const result = await runStalenessReassignment();
      setPopupInfo({
        title: result.ok
          ? "48-Hour Lead Staleness Engine Executed!"
          : "Staleness Rule Failed",
        desc: result.message,
        variant: "info",
      });
    } finally {
      setBusy(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="p-6 max-w-[1600px] mx-auto">
        <p className="text-muted-foreground text-sm">
          WhatsApp campaigns are available to managers only.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-[1600px] mx-auto">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b pb-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground flex items-center gap-2">
            <Send className="w-7 h-7 text-primary" />
            WhatsApp Bulk Broadcast & Drip Engine
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Run targeted WhatsApp campaigns via Meta Cloud API and execute
            48-hour lead staleness auto-reassignment.
            <span className="block mt-1 text-xs text-muted-foreground/90">
              Manage templates in{" "}
              <Link
                to="/settings#whatsapp-templates"
                className="text-primary underline underline-offset-2"
              >
                Settings → WhatsApp templates
              </Link>
              . Use each contact&apos;s WhatsApp thread for 1:1 replies.
            </span>
            {campaignAudience.length === 0 && (
              <span className="ml-1 text-amber-600 dark:text-amber-400">
                No contacts yet — ingest or create leads first.
              </span>
            )}
          </p>
        </div>

        <div className="flex flex-col items-end gap-1">
          <Button
            onClick={handleRunStalenessRule}
            disabled={busy}
            variant="outline"
            className="border-amber-500 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/30"
          >
            <RotateCw className="w-4 h-4 mr-2" />{" "}
            {busy ? "Running…" : "Execute 48-Hr Staleness Rule"}
          </Button>
          <p className="text-xs text-muted-foreground max-w-xs text-right">
            48h reassignment also runs on the scheduled automation cron; this
            button is for manual runs.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-1 border-muted">
          <CardHeader>
            <CardTitle className="text-lg font-bold flex items-center gap-2">
              <Filter className="w-5 h-5 text-primary" />
              Audience Segmentation
            </CardTitle>
            <CardDescription>
              Filter buyers for targeted broadcast campaigns.
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4 text-sm">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-foreground">
                Lead status
              </label>
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="unset">No status set</SelectItem>
                  {noteStatuses.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-foreground">
                Assigned agent
              </label>
              <Select value={selectedAgent} onValueChange={setSelectedAgent}>
                <SelectTrigger>
                  <SelectValue placeholder="All agents" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All agents</SelectItem>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {salesAgents.map((a) => (
                    <SelectItem key={String(a.id)} value={String(a.id)}>
                      {a.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-foreground">
                Preferred location
              </label>
              <Select
                value={selectedLocation}
                onValueChange={setSelectedLocation}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All locations" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All locations</SelectItem>
                  {locationOptions.map((loc) => (
                    <SelectItem key={loc} value={loc}>
                      {loc}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-foreground">
                Budget Bracket
              </label>
              <Select value={selectedBudget} onValueChange={setSelectedBudget}>
                <SelectTrigger>
                  <SelectValue placeholder="All Budgets" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Budgets</SelectItem>
                  <SelectItem value="1_1.5">₹1.0 Cr – ₹1.5 Cr</SelectItem>
                  <SelectItem value="1.5_2">₹1.5 Cr – ₹2.0 Cr</SelectItem>
                  <SelectItem value="2_4">₹2.0 Cr – ₹4.0 Cr</SelectItem>
                  <SelectItem value="4_plus">₹4.0 Cr+</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-foreground">
                Preferred Property Type
              </label>
              <Select value={selectedType} onValueChange={setSelectedType}>
                <SelectTrigger>
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Property Types</SelectItem>
                  <SelectItem value="Flat">Flat / Apartment</SelectItem>
                  <SelectItem value="Villa">Villa</SelectItem>
                  <SelectItem value="Plot">Plot</SelectItem>
                  <SelectItem value="Penthouse">Penthouse</SelectItem>
                  <SelectItem value="Commercial">Commercial</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-foreground">
                Inactivity Threshold
              </label>
              <Select
                value={inactivityFilter}
                onValueChange={setInactivityFilter}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Inactivity Days" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">All contacts</SelectItem>
                  <SelectItem value="7">Inactive ≥ 7 days</SelectItem>
                  <SelectItem value="15">Inactive ≥ 15 days</SelectItem>
                  <SelectItem value="30">Inactive ≥ 30 days</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2 pt-1">
              <Checkbox
                id="require-phone"
                checked={requirePhone}
                onCheckedChange={(v: boolean | "indeterminate") =>
                  setRequirePhone(v === true)
                }
              />
              <label
                htmlFor="require-phone"
                className="text-xs font-semibold text-foreground cursor-pointer"
              >
                Only contacts with a phone number
              </label>
            </div>

            <div className="space-y-1.5 pt-2">
              <label className="text-xs font-semibold text-foreground">
                Campaign template
              </label>
              <Select
                value={selectedTemplateId}
                onValueChange={setSelectedTemplateId}
                disabled={templates.length === 0}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select template" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedTemplate && (
                <div className="space-y-1">
                  <p className="text-[11px] text-muted-foreground line-clamp-3">
                    {selectedTemplate.body}
                  </p>
                  {selectedTemplate.media_url && (
                    <p className="text-[11px] text-primary truncate">
                      Media: {selectedTemplate.media_url}
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="bg-primary/10 p-3 rounded-lg border border-primary/20 flex justify-between items-center text-xs">
              <span className="text-muted-foreground font-semibold">
                Matched Audience:
              </span>
              <Badge className="bg-primary text-primary-foreground font-bold">
                {filteredAudience.length} Contacts
              </Badge>
            </div>

            <Button
              onClick={() => void handleBroadcast()}
              disabled={
                busy || filteredAudience.length === 0 || !selectedTemplate
              }
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold"
            >
              <Send className="w-4 h-4 mr-2" />
              {busy ? "Sending…" : "Dispatch Meta WhatsApp Broadcast"}
            </Button>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2 border-muted">
          <CardHeader>
            <CardTitle className="text-lg font-bold flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              Targeted Audience Preview ({filteredAudience.length})
            </CardTitle>
            <CardDescription>
              {campaignAudience.length === 0
                ? "No contacts in the database yet."
                : "Live contacts matching your segment filters."}
            </CardDescription>
          </CardHeader>

          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-xs text-left">
              <thead className="bg-muted/50 text-muted-foreground uppercase border-b">
                <tr>
                  <th className="p-3">Buyer Name</th>
                  <th className="p-3">Status</th>
                  <th className="p-3">Phone</th>
                  <th className="p-3">Budget</th>
                  <th className="p-3">Type</th>
                  <th className="p-3">Location</th>
                  <th className="p-3">Last Contacted</th>
                  <th className="p-3">Assigned Agent</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredAudience.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="p-6 text-center text-muted-foreground"
                    >
                      No contacts match these filters.
                    </td>
                  </tr>
                ) : (
                  filteredAudience.map((lead) => {
                    const statusLabel =
                      noteStatuses.find((s) => s.value === lead.status)
                        ?.label ||
                      lead.status ||
                      "—";
                    return (
                      <tr key={lead.id} className="hover:bg-muted/30">
                        <td className="p-3 font-bold text-foreground">
                          {lead.name}
                        </td>
                        <td className="p-3">
                          <Badge variant="outline">{statusLabel}</Badge>
                        </td>
                        <td className="p-3">{lead.phone || "—"}</td>
                        <td className="p-3 font-semibold text-primary">
                          {lead.budget}
                        </td>
                        <td className="p-3">
                          <Badge variant="outline">{lead.preferred_type}</Badge>
                        </td>
                        <td className="p-3 text-muted-foreground">
                          {lead.preferred_location || "—"}
                        </td>
                        <td className="p-3 text-muted-foreground">
                          <span className="inline-flex items-center">
                            <Clock className="w-3 h-3 mr-1 text-amber-500" />{" "}
                            {lead.last_contacted_days_ago} days ago
                          </span>
                        </td>
                        <td className="p-3 font-medium">{lead.agent}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>

      <ActionPopupModal
        isOpen={!!popupInfo}
        onClose={() => setPopupInfo(null)}
        title={popupInfo?.title || ""}
        description={popupInfo?.desc || ""}
        variant={popupInfo?.variant}
      />
    </div>
  );
};

export default WhatsAppBroadcast;
